import React, { useCallback, useEffect, useMemo, useState, memo, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Refresh,
  OpenInNew,
  LogoutOutlined,
  Wifi,
  AccessTime,
  PowerSettingsNew,
  Schedule,
  EventBusy,
} from "@mui/icons-material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import QueueOutlinedIcon from "@mui/icons-material/QueueOutlined";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import dayjs from "dayjs";
import { useMutation } from "react-query";
import { Virtuoso, VirtuosoGrid } from "react-virtuoso";
import {
  getConversationsList,
  getHumanOperators,
  getDashboardMetricInsights,
  postForceLogoutUser,
} from "../../api/client";
import { useAuthContext } from "../../context/AuthContext";
import MetricAiInsightsDialog from "../../components/MetricAiInsightsDialog";
import { ensureNamespaceLoaded } from "../../i18n/lazyNamespaces";
import { useTeamSocketPresence } from "../../hooks/useTeamSocketPresence";
import {
  getTeamPresenceKind,
  presenceCardSurface,
  TEAM_PRESENCE_SORT_RANK,
} from "../../utils/teamPresence";
import { getTeamPresenceScreenTitle } from "../../utils/teamPresenceScreenTitle";
import TeamPresenceTabBreakdown from "../../components/TeamPresenceTabBreakdown";

const POLL_MS_FOCUS = 20_000;
const POLL_MS_HIDDEN = 60_000;

/** Altura mínima igual para listas em pares (simetria entre cards). */
const LIST_PANEL_MIN_H = 300;
const LIST_PANEL_MAX_H = 420;
/** Altura fixa por linha na lista virtualizada de conversas (desempenho com muitos itens). */
const CONVERSATION_ROW_HEIGHT = 88;
/** Viewport da área da equipe (grade virtualizada). */
const TEAM_LIST_VIEWPORT_H = 520;
/** Largura mínima de cada quadro na grade (responsivo). */
const TEAM_GRID_MIN_COL_PX = 272;
/** Altura fixa de cada cartão na grade (virtualização estável). */
const TEAM_GRID_CARD_HEIGHT = 400;

function countHumanAssignmentsByUserId(list) {
  const m = new Map();
  for (const c of list) {
    const uid = c.userId ?? c.user_id;
    if (uid == null || uid === 0) continue;
    const n = Number(uid);
    m.set(n, (m.get(n) || 0) + 1);
  }
  return m;
}

/** Lista em CSS Grid dentro do VirtuosoGrid (vários quadros por linha). */
const TeamVirtuosoGridList = forwardRef(function TeamVirtuosoGridList({ style, children, ...rest }, ref) {
  return (
    <div
      ref={ref}
      {...rest}
      style={{
        ...style,
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${TEAM_GRID_MIN_COL_PX}px, 1fr))`,
        columnGap: 12,
        rowGap: 12,
        width: "100%",
        alignContent: "start",
      }}
    >
      {children}
    </div>
  );
});

/** Célula com altura uniforme para o cartão preencher o quadro. */
const TeamVirtuosoGridItem = forwardRef(function TeamVirtuosoGridItem({ style, children, ...rest }, ref) {
  return (
    <div
      ref={ref}
      {...rest}
      style={{
        ...style,
        minWidth: 0,
        height: TEAM_GRID_CARD_HEIGHT,
      }}
    >
      {children}
    </div>
  );
});

function dayjsLocaleForI18n(lang) {
  if (!lang || typeof lang !== "string") return "en";
  if (lang.startsWith("pt")) return "pt-br";
  if (lang.startsWith("es")) return "es";
  if (lang.startsWith("it")) return "it";
  return "en";
}

function formatOperatorTimestamp(val, lang) {
  if (val == null || val === "") return null;
  const d = dayjs(val);
  if (!d.isValid()) return null;
  return d.locale(dayjsLocaleForI18n(lang)).format("L LT");
}

function buildSeriesFromTheme(theme) {
  const p = theme.palette;
  return [
    p.primary.main,
    p.secondary?.main ?? p.info.main,
    p.success.main,
    p.warning.main,
    p.info.main,
    p.error.main,
  ];
}

function statusLabel(code) {
  const map = {
    0: "Pendente",
    1: "Em Atendimento",
    2: "Concluída",
    3: "Contato Futuro",
    4: "Perdida",
    5: "Cancelada",
  };
  const n = typeof code === "string" ? Number(code) : code;
  return map[n] ?? "—";
}

/** Ordem: online → ausente → offline; dentro de cada grupo, nome (locale-aware). */
function compareTeamOperatorsByPresenceAndName(a, b, tabsByUserId) {
  const ta = tabsByUserId.get(Number(a.id)) || 0;
  const tb = tabsByUserId.get(Number(b.id)) || 0;
  const ka = getTeamPresenceKind(a, ta);
  const kb = getTeamPresenceKind(b, tb);
  const ra = TEAM_PRESENCE_SORT_RANK[ka] ?? 99;
  const rb = TEAM_PRESENCE_SORT_RANK[kb] ?? 99;
  if (ra !== rb) return ra - rb;
  return String(a.nome || "").localeCompare(String(b.nome || ""), undefined, { sensitivity: "base" });
}

function getOperatorInitials(name) {
  const s = String(name || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function operatorAvatarSrc(u) {
  const img = u?.imagem;
  return typeof img === "string" && img.trim().length > 0 ? img.trim() : null;
}

/** Mesmas chaves que `StatusSelector` / `theme.palette.status` (pending, inProgress, future). */
function teamWorkloadStatusChipSx(theme, colorKey) {
  const st = theme.palette.status;
  const fallback =
    colorKey === "pending"
      ? theme.palette.success.light
      : colorKey === "inProgress"
        ? theme.palette.warning.main
        : theme.palette.info.light;
  const bg = (st && st[colorKey]) || fallback;
  const fg = theme.palette.getContrastText(bg);
  return {
    cursor: "help",
    fontWeight: 600,
    maxWidth: "100%",
    height: 28,
    bgcolor: bg,
    color: fg,
    border: `1px solid ${alpha(bg, theme.palette.mode === "dark" ? 0.88 : 0.72)}`,
    "& .MuiChip-label": { px: 0.65, py: 0, color: "inherit" },
  };
}

const ConversationRowItem = memo(function ConversationRowItem({ c, onOpen, t }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        height: CONVERSATION_ROW_HEIGHT,
        boxSizing: "border-box",
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <ListItemButton
        onClick={() => onOpen(c.id)}
        sx={{ flex: 1, minWidth: 0, py: 0.75, pr: 0.5, alignItems: "flex-start" }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography component="span" variant="body2" fontWeight={600} noWrap sx={{ maxWidth: "100%" }}>
                {c.name || `#${c.id}`}
              </Typography>
              {Number(c.unread) > 0 ? (
                <Chip size="small" label={c.unread} color="warning" sx={{ height: 22, flexShrink: 0 }} />
              ) : null}
            </Box>
          }
          secondary={
            <Box component="span" sx={{ display: "block", overflow: "hidden" }}>
              <Typography component="span" variant="caption" color="text.secondary" display="block" noWrap>
                {c.channel || c.channelType || "—"} · {statusLabel(c.status)}
                {c.assignedTo ? ` · ${c.assignedTo}` : ""}
                {c.agentName ? ` · ${c.agentName}` : ""}
              </Typography>
              {c.timestamp ? (
                <Typography component="span" variant="caption" color="text.disabled" display="block" noWrap>
                  {dayjs(c.timestamp).format("DD/MM HH:mm")}
                </Typography>
              ) : null}
            </Box>
          }
          primaryTypographyProps={{ component: "div" }}
          secondaryTypographyProps={{ component: "div" }}
        />
      </ListItemButton>
      <Box sx={{ display: "flex", alignItems: "flex-start", pt: 0.5, pr: 0.25, flexShrink: 0 }}>
        <Tooltip title={t("dashboardPage.live.openChat")}>
          <IconButton
            size="small"
            onClick={() => onOpen(c.id)}
            aria-label={t("dashboardPage.live.openChat")}
          >
            <OpenInNew fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
});

function LiveConversationList({ items, emptyLabel, onOpen, t }) {
  if (!items?.length) {
    return (
      <Typography color="text.secondary" variant="body2" sx={{ py: 1 }}>
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <Virtuoso
      data={items}
      style={{ height: "100%", width: "100%" }}
      defaultItemHeight={CONVERSATION_ROW_HEIGHT}
      fixedItemHeight={CONVERSATION_ROW_HEIGHT}
      overscan={{ main: 12, reverse: 12 }}
      computeItemKey={(_, c) => String(c.id)}
      context={{ onOpen, t }}
      itemContent={(_index, c, ctx) => <ConversationRowItem c={c} onOpen={ctx.onOpen} t={ctx.t} />}
    />
  );
}

function LiveKpiCard({ loading, icon, title, value, color, sub, onAi, tChat }) {
  if (loading) {
    return (
      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
        <Skeleton variant="rounded" height={118} sx={{ width: "100%", borderRadius: 1 }} />
      </Grid>
    );
  }
  return (
    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
      <Card
        elevation={1}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          border: 1,
          borderColor: "divider",
          boxShadow: (th) =>
            th.palette.mode === "dark"
              ? `0 1px 4px ${alpha("#000", 0.35)}`
              : `0 1px 3px ${alpha(th.palette.common.black, 0.08)}`,
        }}
      >
        <CardContent sx={{ flex: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: color, width: 44, height: 44 }}>{icon}</Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {title}
                </Typography>
                {onAi ? (
                  <Tooltip title={tChat("analytics.explainAi")}>
                    <IconButton size="small" onClick={onAi} aria-label={tChat("analytics.explainAiAria")}>
                      <AutoAwesomeIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Stack>
              <Typography variant="h4" sx={{ color, lineHeight: 1.15, fontWeight: 600 }}>
                {value}
              </Typography>
              {sub ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  {sub}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}

function LiveListCard({ title, children, footer, onAi, tChat, sampleHint }) {
  return (
    <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
      <Card
        elevation={1}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
          boxShadow: (th) =>
            th.palette.mode === "dark"
              ? `0 1px 4px ${alpha("#000", 0.35)}`
              : `0 1px 3px ${alpha(th.palette.common.black, 0.08)}`,
        }}
      >
        <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", pt: 2, pb: 2, px: 2 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {title}
              </Typography>
            </Box>
            {onAi ? (
              <Tooltip title={tChat("analytics.explainAi")}>
                <IconButton size="small" onClick={onAi} aria-label={tChat("analytics.explainAiAria")}>
                  <AutoAwesomeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
          {sampleHint ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, lineHeight: 1.4 }}>
              {sampleHint}
            </Typography>
          ) : null}
          <Divider />
          <Box
            sx={{
              flex: 1,
              height: LIST_PANEL_MAX_H,
              minHeight: LIST_PANEL_MIN_H,
              overflow: "hidden",
              mt: 1,
            }}
          >
            {children}
          </Box>
          {footer ? <Box sx={{ mt: 1, flexShrink: 0 }}>{footer}</Box> : null}
        </CardContent>
      </Card>
    </Grid>
  );
}

const TeamOperatorCard = memo(
  function TeamOperatorCard({
    u,
    socketTabs,
    presenceDetail,
    screenTitleForPath,
    livePresenceNowMs,
    workPendente,
    workAtendimento,
    workFuturo,
    workSampleLimited,
    myId,
    theme,
    t,
    lang,
    onRequestLogout,
  }) {
    const det = presenceDetail;
    const kind = getTeamPresenceKind(u, socketTabs);
    const surface = presenceCardSurface(theme, kind);
    const uid = Number(u.id);
    const canOfferLogout = myId != null && Number.isFinite(uid) && uid !== myId;
    const lastAccessFmt = formatOperatorTimestamp(u.data_ultimo_acesso, lang);
    const lastLogoutFmt = formatOperatorTimestamp(u.data_logout, lang);
    const presenceLabel =
      kind === "online"
        ? t("dashboardPage.live.onlineNow")
        : kind === "away"
          ? t("dashboardPage.live.away")
          : t("dashboardPage.live.presenceOffline");
    const presenceTip =
      kind === "online"
        ? t("dashboardPage.live.onlinePresenceTooltip")
        : kind === "away"
          ? t("dashboardPage.live.awayPresenceTooltip")
          : t("dashboardPage.live.offlinePresenceTooltip");
    const PresenceIcon = kind === "online" ? Wifi : kind === "away" ? AccessTime : PowerSettingsNew;
    const avatarUrl = operatorAvatarSrc(u);

    return (
      <Card
        variant="outlined"
        sx={{
          height: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderColor: surface.borderColor,
          bgcolor: surface.bgcolor,
          borderLeftWidth: 4,
          borderLeftStyle: "solid",
          borderLeftColor: surface.accent,
          boxShadow: (th) =>
            th.palette.mode === "dark"
              ? `0 1px 4px ${alpha("#000", 0.25)}`
              : `0 1px 2px ${alpha(th.palette.common.black, 0.06)}`,
        }}
      >
        <CardContent
          sx={{
            px: 1.75,
            py: 1.5,
            "&:last-child": { pb: 1.5 },
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ flex: 1, minHeight: 0 }}>
            <Avatar
              src={avatarUrl || undefined}
              alt=""
              sx={{
                width: 44,
                height: 44,
                fontSize: "0.9rem",
                fontWeight: 700,
                border: 2,
                borderColor: surface.accent,
                color: "text.primary",
                bgcolor: (th) => alpha(th.palette.background.paper, 0.85),
                flexShrink: 0,
                mt: 0.125,
              }}
            >
              {getOperatorInitials(u.nome)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={0.75}>
                <Box sx={{ minWidth: 0, flex: 1, pr: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} noWrap title={u.nome} sx={{ lineHeight: 1.3 }}>
                    {u.nome}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    title={u.cargo_nome || u.email || ""}
                    sx={{ display: "block", mt: 0.25, lineHeight: 1.35 }}
                  >
                    {u.cargo_nome || u.email || "—"}
                  </Typography>
                </Box>
                {canOfferLogout ? (
                  <Tooltip title={t("dashboardPage.live.forceLogoutTooltip")}>
                    <IconButton
                      size="small"
                      color="warning"
                      aria-label={t("dashboardPage.live.forceLogoutAction")}
                      onClick={() => onRequestLogout({ id: u.id, nome: u.nome })}
                      sx={{ flexShrink: 0, alignSelf: "flex-start", mt: -0.25, mr: -0.5 }}
                    >
                      <LogoutOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Stack>

              <Stack
                direction="row"
                flexWrap="wrap"
                sx={{ columnGap: 0.75, rowGap: 0.5, alignItems: "center" }}
              >
                <Tooltip title={presenceTip}>
                  <Chip
                    size="small"
                    icon={<PresenceIcon sx={{ "&&": { fontSize: 16 } }} />}
                    label={presenceLabel}
                    color={kind === "online" ? "success" : kind === "away" ? "warning" : "error"}
                    variant="outlined"
                    sx={{
                      cursor: "help",
                      fontWeight: 600,
                      maxWidth: "100%",
                      height: 28,
                      borderColor: surface.accent,
                      bgcolor: (th) => alpha(th.palette.background.paper, th.palette.mode === "dark" ? 0.2 : 0.65),
                      "& .MuiChip-label": { px: 0.65, py: 0 },
                    }}
                  />
                </Tooltip>
              </Stack>

              <Stack
                direction="row"
                flexWrap="wrap"
                sx={{ columnGap: 0.75, rowGap: 0.5, alignItems: "center" }}
              >
                <Tooltip
                  title={
                    workSampleLimited
                      ? `${t("dashboardPage.live.teamWorkloadPendingTooltip")}\n\n${t("dashboardPage.live.teamWorkloadSampleNote")}`
                      : t("dashboardPage.live.teamWorkloadPendingTooltip")
                  }
                >
                  <Chip
                    size="small"
                    label={t("dashboardPage.live.teamWorkloadPendingLabel", { count: workPendente })}
                    variant="filled"
                    sx={teamWorkloadStatusChipSx(theme, "pending")}
                  />
                </Tooltip>
                <Tooltip
                  title={
                    workSampleLimited
                      ? `${t("dashboardPage.live.teamWorkloadAtendimentoTooltip")}\n\n${t("dashboardPage.live.teamWorkloadSampleNote")}`
                      : t("dashboardPage.live.teamWorkloadAtendimentoTooltip")
                  }
                >
                  <Chip
                    size="small"
                    label={t("dashboardPage.live.teamWorkloadAtendimentoLabel", { count: workAtendimento })}
                    color="info"
                    variant="filled"
                    sx={{
                      cursor: "help",
                      fontWeight: 600,
                      maxWidth: "100%",
                      height: 28,
                      "& .MuiChip-label": { px: 0.65, py: 0 },
                    }}
                  />
                </Tooltip>
                <Tooltip
                  title={
                    workSampleLimited
                      ? `${t("dashboardPage.live.teamWorkloadFuturoTooltip")}\n\n${t("dashboardPage.live.teamWorkloadSampleNote")}`
                      : t("dashboardPage.live.teamWorkloadFuturoTooltip")
                  }
                >
                  <Chip
                    size="small"
                    label={t("dashboardPage.live.teamWorkloadFuturoLabel", { count: workFuturo })}
                    variant="filled"
                    sx={teamWorkloadStatusChipSx(theme, "future")}
                  />
                </Tooltip>
              </Stack>

              <TeamPresenceTabBreakdown
                show={kind === "online" && socketTabs > 0}
                socketTabs={socketTabs}
                presenceDetail={det}
                tLive={t}
                screenTitleForPath={screenTitleForPath}
                livePresenceNowMs={livePresenceNowMs}
              />

              <Divider sx={{ borderColor: (th) => alpha(th.palette.divider, 0.55), my: 0 }} />

              <Stack spacing={0.625} sx={{ pt: 0.125 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minHeight: 22 }}>
                  <Box
                    aria-hidden
                    sx={{
                      width: 22,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Schedule sx={{ fontSize: 16, color: "text.secondary", opacity: 0.88 }} />
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.45, minWidth: 0, flex: 1 }}
                    noWrap
                  >
                    {lastAccessFmt
                      ? t("dashboardPage.live.lastAccessLabel", { time: lastAccessFmt })
                      : t("dashboardPage.live.lastAccessMissing")}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minHeight: 22 }}>
                  <Box
                    aria-hidden
                    sx={{
                      width: 22,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <EventBusy sx={{ fontSize: 16, color: "text.secondary", opacity: 0.88 }} />
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.45, minWidth: 0, flex: 1 }}
                    noWrap
                  >
                    {lastLogoutFmt
                      ? t("dashboardPage.live.lastLogoutLabel", { time: lastLogoutFmt })
                      : t("dashboardPage.live.lastLogoutMissing")}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  },
  (a, b) =>
    a.u.id === b.u.id &&
    a.socketTabs === b.socketTabs &&
    a.livePresenceNowMs === b.livePresenceNowMs &&
    JSON.stringify(a.presenceDetail) === JSON.stringify(b.presenceDetail) &&
    a.workPendente === b.workPendente &&
    a.workAtendimento === b.workAtendimento &&
    a.workFuturo === b.workFuturo &&
    a.workSampleLimited === b.workSampleLimited &&
    a.myId === b.myId &&
    a.lang === b.lang &&
    a.u.data_ultimo_acesso === b.u.data_ultimo_acesso &&
    a.u.data_logout === b.u.data_logout &&
    a.u.nome === b.u.nome &&
    a.u.imagem === b.u.imagem &&
    (a.u.cargo_nome || "") === (b.u.cargo_nome || "") &&
    (a.u.email || "") === (b.u.email || "") &&
    a.theme.palette.mode === b.theme.palette.mode
);

export default function DashboardLiveTab() {
  const { t, i18n } = useTranslation("settings");
  const { t: tApp } = useTranslation("app");
  const { t: tChat } = useTranslation("chat");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { userData } = useAuthContext();
  const { tabsByUserId, presenceDetailByUserId, socketConnected } = useTeamSocketPresence(userData);

  const [livePresenceTickAt, setLivePresenceTickAt] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setLivePresenceTickAt(Date.now()), 25000);
    return () => clearInterval(id);
  }, []);

  const screenTitleForPath = useCallback((path) => getTeamPresenceScreenTitle(path, tApp), [tApp]);

  const SERIES = useMemo(() => buildSeriesFromTheme(theme), [theme]);

  const [forceLogoutUserRow, setForceLogoutUserRow] = useState(null);
  const [forceLogoutSubmitting, setForceLogoutSubmitting] = useState(false);
  const [forceLogoutError, setForceLogoutError] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [queue, setQueue] = useState({ list: [], hasMore: false });
  const [humanActive, setHumanActive] = useState({ list: [], hasMore: false });
  const [humanPendente, setHumanPendente] = useState({ list: [], hasMore: false });
  const [humanContatoFuturo, setHumanContatoFuturo] = useState({ list: [], hasMore: false });
  const [botFlow, setBotFlow] = useState({ list: [], hasMore: false });
  const [aiFlow, setAiFlow] = useState({ list: [], hasMore: false });
  const [unreadQueue, setUnreadQueue] = useState({ list: [], hasMore: false });
  const [operators, setOperators] = useState([]);

  const [metricDialogOpen, setMetricDialogOpen] = useState(false);
  const [metricDialogMetric, setMetricDialogMetric] = useState(null);
  const [metricDialogName, setMetricDialogName] = useState("");
  const [metricDialogLabel, setMetricDialogLabel] = useState("");
  const [metricDialogHuman, setMetricDialogHuman] = useState("");
  const [metricDialogData, setMetricDialogData] = useState(null);
  const [metricDialogError, setMetricDialogError] = useState(null);
  const [metricDialogBasePayload, setMetricDialogBasePayload] = useState(null);

  const todayStr = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

  useEffect(() => {
    ensureNamespaceLoaded("chat");
  }, []);

  const onlineCount = useMemo(() => {
    let n = 0;
    for (const u of operators) {
      if ((tabsByUserId.get(Number(u.id)) || 0) > 0) n += 1;
    }
    return n;
  }, [operators, tabsByUserId]);

  const teamPresenceCounts = useMemo(() => {
    let away = 0;
    let offline = 0;
    for (const u of operators) {
      const tabs = tabsByUserId.get(Number(u.id)) || 0;
      const k = getTeamPresenceKind(u, tabs);
      if (k === "away") away += 1;
      if (k === "offline") offline += 1;
    }
    return { away, offline };
  }, [operators, tabsByUserId]);

  const operatorsSortedForTeamGrid = useMemo(() => {
    if (!operators.length) return operators;
    return [...operators].sort((a, b) => compareTeamOperatorsByPresenceAndName(a, b, tabsByUserId));
  }, [operators, tabsByUserId]);

  const liveSnapshotForAi = useMemo(() => {
    return {
      filaPendente: queue.list.length,
      filaHasMore: queue.hasMore,
      emAtendimentoHumano: humanActive.list.length,
      humanoHasMore: humanActive.hasMore,
      pendenteHumanoAtribuido: humanPendente.list.length,
      pendenteHumanoHasMore: humanPendente.hasMore,
      contatoFuturoHumanoAtribuido: humanContatoFuturo.list.length,
      contatoFuturoHumanoHasMore: humanContatoFuturo.hasMore,
      chatbotAbertos: botFlow.list.length,
      chatbotHasMore: botFlow.hasMore,
      agenteIaAbertos: aiFlow.list.length,
      agenteHasMore: aiFlow.hasMore,
      operadoresOnline: onlineCount,
      operadoresOnlineSocket: onlineCount,
      operadoresAusentes: teamPresenceCounts.away,
      operadoresOfflineLogout: teamPresenceCounts.offline,
      operadoresTotal: operators.length,
      socketConectadoNestePainel: socketConnected,
      filaNaoLidas: unreadQueue.list.length,
      atualizadoEm: updatedAt ? new Date(updatedAt).toISOString() : null,
    };
  }, [
    queue,
    humanActive,
    humanPendente,
    humanContatoFuturo,
    botFlow,
    aiFlow,
    unreadQueue,
    operators,
    updatedAt,
    onlineCount,
    teamPresenceCounts,
    socketConnected,
  ]);

  const metricInsightsMutation = useMutation(
    async (cfg) => {
      const payload = {
        metric: cfg.metric,
        metricName: cfg.metricName,
        metricDefinition: cfg.metricDefinition,
        metricValue: cfg.metricValue,
        metricHuman: cfg.metricHuman,
        period: { start: todayStr, end: todayStr },
        summaryContext: {
          painelAoVivo: true,
          snapshot: cfg.fullSnapshot ?? liveSnapshotForAi,
        },
      };
      return getDashboardMetricInsights("atendimento", payload);
    },
    {
      onSuccess: (data) => {
        setMetricDialogData(data || null);
        setMetricDialogError(null);
      },
      onError: (err) => {
        const msg =
          (typeof err?.message === "string" && err.message) ||
          (typeof err?.error === "string" && err.error) ||
          t("dashboardPage.live.aiError");
        setMetricDialogError(msg);
      },
    }
  );

  const openAi = useCallback(
    (cfg) => {
      setMetricDialogMetric(cfg.metric);
      setMetricDialogName(cfg.metricName || "");
      setMetricDialogLabel(cfg.metricLabel || "");
      setMetricDialogHuman(cfg.metricHuman || "");
      setMetricDialogBasePayload(cfg);
      setMetricDialogOpen(true);
      setMetricDialogData(null);
      setMetricDialogError(null);
      metricInsightsMutation.mutate({ ...cfg, fullSnapshot: liveSnapshotForAi });
    },
    [metricInsightsMutation, liveSnapshotForAi]
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const [queueRes, humanRes, humanPendenteRes, humanFuturoRes, botRes, aiRes, unreadRes, opRes] =
        await Promise.all([
        getConversationsList({
          filters: ["Fila", "Pendente"],
          limit: 50,
          default_date_window_days: 0,
        }),
        getConversationsList({
          filters: ["Em Atendimento"],
          tipo_responsavel: "humanos",
          limit: 50,
          default_date_window_days: 0,
        }),
        getConversationsList({
          filters: ["Pendente"],
          tipo_responsavel: "humanos",
          limit: 50,
          default_date_window_days: 0,
        }),
        getConversationsList({
          filters: ["Contato Futuro"],
          tipo_responsavel: "humanos",
          limit: 50,
          default_date_window_days: 0,
        }),
        getConversationsList({
          filters: ["Pendente", "Em Atendimento"],
          tipo_responsavel: "chatbots",
          limit: 50,
          default_date_window_days: 0,
        }),
        getConversationsList({
          filters: ["Pendente", "Em Atendimento"],
          tipo_responsavel: "agentes",
          limit: 50,
          default_date_window_days: 0,
        }),
        getConversationsList({
          filters: ["Fila", "Pendente", "Não Lidas"],
          limit: 50,
          default_date_window_days: 0,
        }),
        getHumanOperators(200),
      ]);

      setQueue({ list: queueRes.list, hasMore: !!queueRes.pagination?.hasMore });
      setHumanActive({ list: humanRes.list, hasMore: !!humanRes.pagination?.hasMore });
      setHumanPendente({ list: humanPendenteRes.list, hasMore: !!humanPendenteRes.pagination?.hasMore });
      setHumanContatoFuturo({ list: humanFuturoRes.list, hasMore: !!humanFuturoRes.pagination?.hasMore });
      setBotFlow({ list: botRes.list, hasMore: !!botRes.pagination?.hasMore });
      setAiFlow({ list: aiRes.list, hasMore: !!aiRes.pagination?.hasMore });
      setUnreadQueue({ list: unreadRes.list, hasMore: !!unreadRes.pagination?.hasMore });
      setOperators(Array.isArray(opRes.operators) ? opRes.operators : []);
      setUpdatedAt(new Date());
    } catch (e) {
      console.error(e);
      setError(t("dashboardPage.live.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const [pollIntervalMs, setPollIntervalMs] = useState(POLL_MS_FOCUS);

  useEffect(() => {
    const fn = () => setPollIntervalMs(document.hidden ? POLL_MS_HIDDEN : POLL_MS_FOCUS);
    fn();
    document.addEventListener("visibilitychange", fn);
    return () => document.removeEventListener("visibilitychange", fn);
  }, []);

  useEffect(() => {
    const id = setInterval(load, pollIntervalMs);
    return () => clearInterval(id);
  }, [load, pollIntervalMs]);

  const openChat = useCallback(
    (conversationId) => {
      if (conversationId == null) return;
      navigate(`/chat/${encodeURIComponent(String(conversationId))}`);
    },
    [navigate]
  );

  const confirmForceLogout = useCallback(async () => {
    const row = forceLogoutUserRow;
    if (!row?.id) return;
    setForceLogoutError(null);
    setForceLogoutSubmitting(true);
    try {
      await postForceLogoutUser(row.id);
      setForceLogoutUserRow(null);
      await load();
    } catch (e) {
      const msg =
        (e && typeof e === "object" && e.message) ||
        (typeof e === "string" ? e : t("dashboardPage.live.forceLogoutError"));
      setForceLogoutError(msg);
    } finally {
      setForceLogoutSubmitting(false);
    }
  }, [forceLogoutUserRow, load, t]);

  const humanAtendimentoByUserId = useMemo(
    () => countHumanAssignmentsByUserId(humanActive.list),
    [humanActive.list]
  );
  const humanPendenteByUserId = useMemo(
    () => countHumanAssignmentsByUserId(humanPendente.list),
    [humanPendente.list]
  );
  const humanFuturoByUserId = useMemo(
    () => countHumanAssignmentsByUserId(humanContatoFuturo.list),
    [humanContatoFuturo.list]
  );
  const humanWorkloadSampleLimited = useMemo(
    () => humanActive.hasMore || humanPendente.hasMore || humanContatoFuturo.hasMore,
    [humanActive.hasMore, humanPendente.hasMore, humanContatoFuturo.hasMore]
  );

  const timeFmt = updatedAt
    ? dayjs(updatedAt).locale(i18n.language?.startsWith("pt") ? "pt-br" : "en").format("L LT")
    : "—";

  const myId = userData?.id != null ? Number(userData.id) : null;

  const requestTeamLogout = useCallback((row) => {
    setForceLogoutError(null);
    setForceLogoutUserRow(row);
  }, []);

  const teamVirtuosoCtx = useMemo(
    () => ({
      tabsByUserId,
      presenceDetailByUserId,
      livePresenceTickAt,
      screenTitleForPath,
      humanPendenteByUserId,
      humanAtendimentoByUserId,
      humanFuturoByUserId,
      humanWorkloadSampleLimited,
      myId,
      theme,
      t,
      lang: i18n.language,
      onRequestLogout: requestTeamLogout,
    }),
    [
      tabsByUserId,
      presenceDetailByUserId,
      livePresenceTickAt,
      screenTitleForPath,
      humanPendenteByUserId,
      humanAtendimentoByUserId,
      humanFuturoByUserId,
      humanWorkloadSampleLimited,
      myId,
      theme,
      t,
      i18n.language,
      requestTeamLogout,
    ]
  );

  const qVal = `${queue.list.length}${queue.hasMore ? "+" : ""}`;
  const hVal = `${humanActive.list.length}${humanActive.hasMore ? "+" : ""}`;
  const onVal = `${onlineCount}/${operators.length}`;
  const autoTotal = botFlow.list.length + aiFlow.list.length;
  const autoVal = `${autoTotal}${botFlow.hasMore || aiFlow.hasMore ? "+" : ""}`;

  const aiOverviewCfg = useMemo(
    () => ({
      metric: "painel_ao_vivo_visao",
      metricName: t("dashboardPage.live.aiOverviewMetricName"),
      metricLabel: t("dashboardPage.tabLive"),
      metricDefinition: t("dashboardPage.live.aiOverviewDefinition"),
      metricValue: liveSnapshotForAi,
      metricHuman: "",
    }),
    [t, liveSnapshotForAi]
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            {t("dashboardPage.live.pollHint")}
          </Typography>
          {pollIntervalMs > POLL_MS_FOCUS ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {t("dashboardPage.live.pollSlowerWhenHidden")}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.disabled">
            {t("dashboardPage.live.lastUpdate", { time: timeFmt })}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={isMobile ? "stretch" : "flex-end"}>
          <Tooltip title={tChat("analytics.explainAi")}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => openAi(aiOverviewCfg)}
              disabled={loading && !updatedAt}
            >
              {t("dashboardPage.live.aiExplainPanel")}
            </Button>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<Refresh />}
            onClick={() => {
              setLoading(true);
              load();
            }}
            disabled={loading}
          >
            {t("dashboardPage.live.refresh")}
          </Button>
        </Stack>
      </Box>

      {loading && !updatedAt ? <LinearProgress sx={{ mb: 2 }} /> : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Dialog
        open={Boolean(forceLogoutUserRow)}
        onClose={() => !forceLogoutSubmitting && setForceLogoutUserRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("dashboardPage.live.forceLogoutTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: "pre-line", lineHeight: 1.55 }}>
            {t("dashboardPage.live.forceLogoutBody", { name: forceLogoutUserRow?.nome || "" })}
          </Typography>
          {forceLogoutError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {forceLogoutError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceLogoutUserRow(null)} disabled={forceLogoutSubmitting}>
            {t("dashboardPage.live.forceLogoutCancel")}
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={confirmForceLogout}
            disabled={forceLogoutSubmitting}
          >
            {t("dashboardPage.live.forceLogoutConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <LiveKpiCard
          loading={loading && !updatedAt}
          icon={<QueueOutlinedIcon />}
          title={t("dashboardPage.live.kpiQueue")}
          value={qVal}
          color={SERIES[0]}
          sub={
            unreadQueue.list.length > 0
              ? t("dashboardPage.live.kpiQueueUnreadHint", {
                  count: unreadQueue.list.length,
                  more: unreadQueue.hasMore ? "+" : "",
                })
              : undefined
          }
          onAi={() =>
            openAi({
              metric: "ao_vivo_fila",
              metricName: t("dashboardPage.live.queueTitle"),
              metricLabel: t("dashboardPage.live.kpiQueue"),
              metricDefinition: t("dashboardPage.live.aiQueueDefinition"),
              metricValue: { count: queue.list.length, hasMore: queue.hasMore, amostraIds: queue.list.slice(0, 8).map((x) => x.id) },
              metricHuman: qVal,
            })
          }
          tChat={tChat}
        />
        <LiveKpiCard
          loading={loading && !updatedAt}
          icon={<SupportAgentIcon />}
          title={t("dashboardPage.live.kpiHumanActive")}
          value={hVal}
          color={SERIES[1]}
          onAi={() =>
            openAi({
              metric: "ao_vivo_humano",
              metricName: t("dashboardPage.live.humanTitle"),
              metricLabel: t("dashboardPage.live.kpiHumanActive"),
              metricDefinition: t("dashboardPage.live.aiHumanDefinition"),
              metricValue: {
                count: humanActive.list.length,
                hasMore: humanActive.hasMore,
                amostra: humanActive.list.slice(0, 6).map((x) => ({ id: x.id, nome: x.name })),
              },
              metricHuman: hVal,
            })
          }
          tChat={tChat}
        />
        <LiveKpiCard
          loading={loading && !updatedAt}
          icon={<GroupsOutlinedIcon />}
          title={t("dashboardPage.live.kpiOnline")}
          value={onVal}
          color={SERIES[2]}
          sub={t("dashboardPage.live.kpiOnlineSub", { online: onlineCount, total: operators.length })}
          onAi={() =>
            openAi({
              metric: "ao_vivo_equipe_online",
              metricName: t("dashboardPage.live.kpiOnline"),
              metricLabel: t("dashboardPage.live.teamTitle"),
              metricDefinition: t("dashboardPage.live.aiOnlineDefinition"),
              metricValue: {
                onlineNoSocket: onlineCount,
                total: operators.length,
                ausentes: teamPresenceCounts.away,
                offlineLogout: teamPresenceCounts.offline,
                socketDestePainel: socketConnected,
              },
              metricHuman: onVal,
            })
          }
          tChat={tChat}
        />
        <LiveKpiCard
          loading={loading && !updatedAt}
          icon={<HubOutlinedIcon />}
          title={t("dashboardPage.live.kpiAutomation")}
          value={autoVal}
          color={SERIES[3]}
          sub={t("dashboardPage.live.kpiAutomationSub", {
            bot: botFlow.list.length,
            ai: aiFlow.list.length,
          })}
          onAi={() =>
            openAi({
              metric: "ao_vivo_automacao",
              metricName: t("dashboardPage.live.kpiAutomation"),
              metricLabel: t("dashboardPage.live.kpiAutomation"),
              metricDefinition: t("dashboardPage.live.aiAutomationDefinition"),
              metricValue: {
                chatbot: botFlow.list.length,
                chatbotHasMore: botFlow.hasMore,
                agenteIa: aiFlow.list.length,
                agenteHasMore: aiFlow.hasMore,
              },
              metricHuman: autoVal,
            })
          }
          tChat={tChat}
        />
      </Grid>

      {unreadQueue.list.length > 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("dashboardPage.live.unreadQueueBanner", {
            count: unreadQueue.list.length,
            more: unreadQueue.hasMore ? "+" : "",
          })}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <LiveListCard
          title={t("dashboardPage.live.queueTitle")}
          sampleHint={t("dashboardPage.live.listVirtualizedSample", {
            count: queue.list.length,
            more: queue.hasMore ? "+" : "",
          })}
          onAi={() =>
            openAi({
              metric: "ao_vivo_lista_fila",
              metricName: t("dashboardPage.live.queueTitle"),
              metricDefinition: t("dashboardPage.live.aiQueueListDefinition"),
              metricValue: queue.list.slice(0, 15).map((c) => ({
                id: c.id,
                cliente: c.name,
                canal: c.channel || c.channelType,
                status: c.status,
              })),
              metricHuman: "",
            })
          }
          tChat={tChat}
          footer={
            queue.hasMore ? (
              <Typography variant="caption" color="text.secondary">
                {t("dashboardPage.live.moreNotListed")}
              </Typography>
            ) : null
          }
        >
          <LiveConversationList
            items={queue.list}
            emptyLabel={t("dashboardPage.live.empty")}
            onOpen={openChat}
            t={t}
          />
        </LiveListCard>

        <LiveListCard
          title={t("dashboardPage.live.humanTitle")}
          sampleHint={t("dashboardPage.live.listVirtualizedSample", {
            count: humanActive.list.length,
            more: humanActive.hasMore ? "+" : "",
          })}
          onAi={() =>
            openAi({
              metric: "ao_vivo_lista_humano",
              metricName: t("dashboardPage.live.humanTitle"),
              metricDefinition: t("dashboardPage.live.aiHumanListDefinition"),
              metricValue: humanActive.list.slice(0, 15).map((c) => ({
                id: c.id,
                cliente: c.name,
                operador: c.assignedTo,
              })),
              metricHuman: "",
            })
          }
          tChat={tChat}
          footer={
            humanActive.hasMore ? (
              <Typography variant="caption" color="text.secondary">
                {t("dashboardPage.live.moreNotListed")}
              </Typography>
            ) : null
          }
        >
          <LiveConversationList
            items={humanActive.list}
            emptyLabel={t("dashboardPage.live.empty")}
            onOpen={openChat}
            t={t}
          />
        </LiveListCard>

        <LiveListCard
          title={t("dashboardPage.live.botTitle")}
          sampleHint={t("dashboardPage.live.listVirtualizedSample", {
            count: botFlow.list.length,
            more: botFlow.hasMore ? "+" : "",
          })}
          onAi={() =>
            openAi({
              metric: "ao_vivo_lista_bot",
              metricName: t("dashboardPage.live.botTitle"),
              metricDefinition: t("dashboardPage.live.aiBotListDefinition"),
              metricValue: botFlow.list.slice(0, 15).map((c) => ({
                id: c.id,
                cliente: c.name,
              })),
              metricHuman: "",
            })
          }
          tChat={tChat}
          footer={
            botFlow.hasMore ? (
              <Typography variant="caption" color="text.secondary">
                {t("dashboardPage.live.moreNotListed")}
              </Typography>
            ) : null
          }
        >
          <LiveConversationList
            items={botFlow.list}
            emptyLabel={t("dashboardPage.live.empty")}
            onOpen={openChat}
            t={t}
          />
        </LiveListCard>

        <LiveListCard
          title={t("dashboardPage.live.aiTitle")}
          sampleHint={t("dashboardPage.live.listVirtualizedSample", {
            count: aiFlow.list.length,
            more: aiFlow.hasMore ? "+" : "",
          })}
          onAi={() =>
            openAi({
              metric: "ao_vivo_lista_ia",
              metricName: t("dashboardPage.live.aiTitle"),
              metricDefinition: t("dashboardPage.live.aiAgentListDefinition"),
              metricValue: aiFlow.list.slice(0, 15).map((c) => ({
                id: c.id,
                cliente: c.name,
                agente: c.agentName,
              })),
              metricHuman: "",
            })
          }
          tChat={tChat}
          footer={
            aiFlow.hasMore ? (
              <Typography variant="caption" color="text.secondary">
                {t("dashboardPage.live.moreNotListed")}
              </Typography>
            ) : null
          }
        >
          <LiveConversationList
            items={aiFlow.list}
            emptyLabel={t("dashboardPage.live.empty")}
            onOpen={openChat}
            t={t}
          />
        </LiveListCard>

        <Grid item xs={12} sx={{ display: "flex" }}>
          <Card
            elevation={1}
            sx={{
              width: "100%",
              border: 1,
              borderColor: "divider",
              boxShadow: (th) =>
                th.palette.mode === "dark"
                  ? `0 1px 4px ${alpha("#000", 0.35)}`
                  : `0 1px 3px ${alpha(th.palette.common.black, 0.08)}`,
            }}
          >
            <CardContent sx={{ pt: 2 }}>
              <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t("dashboardPage.live.teamTitle")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("dashboardPage.live.teamSubtitle")}
                  </Typography>
                  {operators.length > 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.45 }}>
                      {t("dashboardPage.live.teamMemberCount", { count: operators.length })}
                    </Typography>
                  ) : null}
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, lineHeight: 1.45 }}>
                    {t("dashboardPage.live.teamPresenceNote")}
                  </Typography>
                  {operators.length > 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.45 }}>
                      {t("dashboardPage.live.teamListVirtualizedHint")}
                    </Typography>
                  ) : null}
                </Box>
                <Tooltip title={tChat("analytics.explainAi")}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      openAi({
                        metric: "ao_vivo_equipe",
                        metricName: t("dashboardPage.live.teamTitle"),
                        metricDefinition: t("dashboardPage.live.aiTeamDefinition"),
                        metricValue: operatorsSortedForTeamGrid.slice(0, 40).map((u) => {
                          const st = tabsByUserId.get(Number(u.id)) || 0;
                          return {
                            id: u.id,
                            nome: u.nome,
                            presenca: getTeamPresenceKind(u, st),
                            abasNoSocket: st,
                            data_ultimo_acesso: u.data_ultimo_acesso ?? null,
                            data_logout: u.data_logout ?? null,
                          };
                        }),
                        metricHuman: "",
                      })
                    }
                    aria-label={tChat("analytics.explainAiAria")}
                  >
                    <AutoAwesomeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Divider sx={{ mb: 1.5 }} />
              {!socketConnected ? (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  {t("dashboardPage.live.teamSocketDisconnected")}
                </Alert>
              ) : null}
              {!operators.length ? (
                <Typography color="text.secondary" variant="body2">
                  {t("dashboardPage.live.empty")}
                </Typography>
              ) : (
                <Box sx={{ width: "100%", height: TEAM_LIST_VIEWPORT_H }}>
                  <VirtuosoGrid
                    data={operatorsSortedForTeamGrid}
                    style={{ height: "100%", width: "100%" }}
                    components={{ List: TeamVirtuosoGridList, Item: TeamVirtuosoGridItem }}
                    computeItemKey={(_, u) => String(u.id)}
                    context={teamVirtuosoCtx}
                    overscan={8}
                    itemContent={(_index, u, ctx) => {
                      const socketTabs = ctx.tabsByUserId.get(Number(u.id)) || 0;
                      const uid = Number(u.id);
                      return (
                        <TeamOperatorCard
                          u={u}
                          socketTabs={socketTabs}
                          presenceDetail={ctx.presenceDetailByUserId.get(uid)}
                          screenTitleForPath={ctx.screenTitleForPath}
                          livePresenceNowMs={ctx.livePresenceTickAt}
                          workPendente={ctx.humanPendenteByUserId.get(uid) || 0}
                          workAtendimento={ctx.humanAtendimentoByUserId.get(uid) || 0}
                          workFuturo={ctx.humanFuturoByUserId.get(uid) || 0}
                          workSampleLimited={ctx.humanWorkloadSampleLimited}
                          myId={ctx.myId}
                          theme={ctx.theme}
                          t={ctx.t}
                          lang={ctx.lang}
                          onRequestLogout={ctx.onRequestLogout}
                        />
                      );
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <MetricAiInsightsDialog
        open={metricDialogOpen}
        metric={metricDialogMetric}
        metricName={metricDialogName}
        metricLabel={metricDialogLabel}
        metricHuman={metricDialogHuman}
        period={{ start: todayStr, end: todayStr }}
        loading={metricInsightsMutation.isLoading}
        error={metricDialogError}
        data={metricDialogData}
        scope="atendimento"
        basePayload={metricDialogBasePayload}
        onClose={() => setMetricDialogOpen(false)}
      />
    </Box>
  );
}